"use client";

import { useState, useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import styled from "styled-components";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatMoney, formatPercent } from "@/utils/format";
import { TrendingUp, TrendingDown, ChevronRight, Package } from "lucide-react";
import type { UnifiedTopProduct } from "./types";

interface TopProductsProps {
  products: UnifiedTopProduct[];
  isLoading: boolean;
}

const TOP_LIMIT = 5;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  & > * {
    min-width: 0;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledIcon = styled.span<{ $color: string }>`
  color: ${({ $color }) => $color};
  display: flex;
  align-items: center;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const TitleText = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: var(--color-text-secondary);
`;

const EmptyState = styled.div`
  text-align: center;
  color: var(--color-text-tertiary);
  padding: 24px 0;
`;

const ProductsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProductLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border-radius: 8px;
  text-decoration: none;
  transition: background-color 0.15s ease;
  min-width: 0;

  &:hover {
    background-color: var(--color-bg-page);
  }
`;

const RankBadge = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: var(--color-bg-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  color: var(--color-text-secondary);
`;

const ImageWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const PlaceholderImage = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 6px;
  background-color: var(--color-bg-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-tertiary);

  svg {
    width: 20px;
    height: 20px;
  }
`;

const StyledImage = styled(Image)`
  border-radius: 6px;
  object-fit: cover;
`;

const MarketplaceBadge = styled.span<{ $isOzon: boolean }>`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 500;
  color: var(--color-text-inverse);
  background-color: ${({ $isOzon }) => ($isOzon ? "var(--color-accent-ring)" : "var(--color-wb)")};
`;

const ProductInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProductName = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProductMeta = styled.p`
  font-size: 12px;
  color: var(--color-text-secondary);
  margin: 0;
`;

const ProfitSection = styled.div`
  text-align: right;
  flex-shrink: 0;
`;

const ProfitValue = styled.p<{ $positive: boolean }>`
  font-size: 14px;
  font-weight: 600;
  margin: 0;
  color: ${({ $positive }) => ($positive ? "var(--color-success)" : "var(--color-error)")};
`;

const ProfitPercent = styled.p`
  font-size: 12px;
  color: var(--color-text-tertiary);
  margin: 0;
`;

const ChevronIcon = styled(ChevronRight)`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  color: var(--color-border-input);
`;

const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
`;

const SkeletonInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SkeletonProfit = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-end;
`;

export function TopProducts({ products, isLoading }: TopProductsProps) {
  // Сортируем по прибыли и берём топ прибыльных/убыточных
  const { topProfitable, topUnprofitable } = useMemo(() => {
    // Убираем дубликаты по id
    const unique = new Map<string, UnifiedTopProduct>();
    for (const p of products) {
      if (!unique.has(p.id)) {
        unique.set(p.id, p);
      }
    }
    const all = Array.from(unique.values());

    // Сортировка по убыванию прибыли
    const sorted = [...all].sort((a, b) => b.profit - a.profit);

    // Топ прибыльные — первые 5 с наибольшей прибылью
    const profitable = sorted.slice(0, TOP_LIMIT);

    // Топ убыточные — последние 5 с наименьшей прибылью (без пересечения с прибыльными)
    const profitableIds = new Set(profitable.map(p => p.id));
    const unprofitable = [...sorted]
      .reverse()
      .filter(p => !profitableIds.has(p.id))
      .slice(0, TOP_LIMIT);

    return { topProfitable: profitable, topUnprofitable: unprofitable };
  }, [products]);

  if (isLoading) {
    return (
      <Grid>
        <ProductListSkeleton title="Топ прибыльные" />
        <ProductListSkeleton title="Топ убыточные" />
      </Grid>
    );
  }

  return (
    <Grid>
      <ProductList
        title="Топ прибыльные"
        icon={TrendingUp}
        iconColor="var(--color-success)"
        products={topProfitable}
        emptyMessage="Нет данных о прибыльных товарах"
      />
      <ProductList
        title="Топ убыточные"
        icon={TrendingDown}
        iconColor="var(--color-error)"
        products={topUnprofitable}
        emptyMessage="Нет данных об убыточных товарах"
      />
    </Grid>
  );
}

interface ProductListProps {
  title: string;
  icon: React.ElementType;
  iconColor: string;
  products: UnifiedTopProduct[];
  emptyMessage: string;
}

function ProductList({ title, icon: Icon, iconColor, products, emptyMessage }: ProductListProps) {
  return (
    <Card>
      <CardHeader>
        <HeaderRow>
          <StyledIcon $color={iconColor}>
            <Icon />
          </StyledIcon>
          <TitleText>{title}</TitleText>
        </HeaderRow>
      </CardHeader>
      <CardContent>
        {products.length === 0 ? (
          <EmptyState>{emptyMessage}</EmptyState>
        ) : (
          <ProductsList>
            {products.map((product, index) => (
              <ProductItem key={product.id} product={product} rank={index + 1} />
            ))}
          </ProductsList>
        )}
      </CardContent>
    </Card>
  );
}

interface ProductImageProps {
  src?: string | null;
  alt: string;
}

function ProductImage({ src, alt }: ProductImageProps) {
  const [hasError, setHasError] = useState(false);

  if (!src || hasError) {
    return (
      <PlaceholderImage>
        <Package />
      </PlaceholderImage>
    );
  }

  return (
    <StyledImage
      src={src}
      alt={alt}
      width={52}
      height={52}
      unoptimized
      onError={() => setHasError(true)}
    />
  );
}

interface ProductItemProps {
  product: UnifiedTopProduct;
  rank: number;
}

function ProductItem({ product, rank }: ProductItemProps) {
  const isOzon = product.marketplace === "ozon";

  return (
    <ProductLink href={product.product_url}>
      <RankBadge>{rank}</RankBadge>

      <ImageWrapper>
        <ProductImage src={product.image_url} alt={product.name} />
        <MarketplaceBadge $isOzon={isOzon} title={isOzon ? "Ozon" : "Wildberries"}>
          {isOzon ? "O" : "W"}
        </MarketplaceBadge>
      </ImageWrapper>

      <ProductInfo>
        <ProductName>{product.name}</ProductName>
        <ProductMeta>
          {product.sku || "\u2014"} · {product.sales_count || 0} продаж
        </ProductMeta>
      </ProductInfo>

      <ProfitSection>
        <ProfitValue $positive={product.profit >= 0}>{formatMoney(product.profit_display)}</ProfitValue>
        <ProfitPercent>{formatPercent(product.roi_display)}</ProfitPercent>
      </ProfitSection>

      <ChevronIcon />
    </ProductLink>
  );
}

function ProductListSkeleton({ title }: { title: string }) {
  return (
    <Card>
      <CardHeader>
        <TitleText>{title}</TitleText>
      </CardHeader>
      <CardContent>
        <ProductsList>
          {[1, 2, 3, 4, 5].map((i) => (
            <SkeletonRow key={i}>
              <Skeleton width={24} height={24} borderRadius="50%" />
              <Skeleton width={52} height={52} borderRadius={6} />
              <SkeletonInfo>
                <Skeleton width={128} height={16} />
                <Skeleton width={80} height={12} />
              </SkeletonInfo>
              <SkeletonProfit>
                <Skeleton width={64} height={16} />
                <Skeleton width={40} height={12} />
              </SkeletonProfit>
            </SkeletonRow>
          ))}
        </ProductsList>
      </CardContent>
    </Card>
  );
}
